import { reactive } from 'vue';

import { client } from '@/shared/api/apiClient';
import { addExperienceToFavorite, removeExperienceFromFavorite } from '@/shared/api/generated/api';

const favoriteIds = reactive(new Set<number>());
const pendingIds = reactive(new Set<number>());

const FAVORITE_KEYS = ['is_favorite', 'is_in_favorites', 'in_favorites', 'isInFavorites'] as const;

const readFavoriteFlag = (item: unknown): boolean => {
  if (!item || typeof item !== 'object') return false;

  const record = item as Record<string, unknown>;

  return FAVORITE_KEYS.some((key) => record[key] === true);
};

export const useFavorites = () => {
  const isFavorite = (id?: number | null): boolean => (typeof id === 'number' ? favoriteIds.has(id) : false);

  const seedFavorites = (items: readonly unknown[]) => {
    items.forEach((item) => {
      if (!item || typeof item !== 'object') return;

      const { id } = item as { id?: unknown };

      if (typeof id === 'number' && readFavoriteFlag(item)) {
        favoriteIds.add(id);
      }
    });
  };

  const toggleFavorite = async (id: number) => {
    if (typeof id !== 'number' || pendingIds.has(id)) return;

    const wasFavorite = favoriteIds.has(id);

    // Оптимистичное обновление
    if (wasFavorite) {
      favoriteIds.delete(id);
    } else {
      favoriteIds.add(id);
    }

    pendingIds.add(id);

    try {
      if (wasFavorite) {
        await removeExperienceFromFavorite({ path: { id }, client, throwOnError: true });
      } else {
        await addExperienceToFavorite({ path: { id }, client, throwOnError: true });
      }
    } catch {
      // Откат при ошибке
      if (wasFavorite) {
        favoriteIds.add(id);
      } else {
        favoriteIds.delete(id);
      }
    } finally {
      pendingIds.delete(id);
    }
  };

  return { isFavorite, seedFavorites, toggleFavorite };
};
